import {consumerCartApi, interviewApi, surveyApi} from '@/container';
import {acceptHMRUpdate, defineStore} from "pinia";
import {ConsumerSaleProduct} from "@/product/interfaces/Product";
import {Interview, InterviewInstance, Transcript, Utterance} from "@/interview/interfaces/interview";
import {PrefilledSurveyAnswer, Survey} from "@/core/interfaces/survey/Survey";
import {usePatientStore} from "@/stores/patient.store";
import {Backing, BackingInterviewStep} from "@/backing/interfaces/backing";
import {Attachment} from "@/attachment/interfaces/attachment";
import {ConsumerCart} from "@/core/http/resources/ConsumerCartResource";
import {Program} from "@/program/interfaces/Program";

export const useInterviewStore = defineStore('interview', {
    state: () => ({
        interviewId: null as number | null,
        audio: null as Blob | null,
        interview: null as Interview | null,
        interviewInstance: null as InterviewInstance | null,
        attachments: null as Attachment[] | null,
        surveys: null as Survey[] | null,
        products: null as ConsumerSaleProduct[] | null,
        carts: null as ConsumerCart[] | null,
        cartAfterKey: null as string | null,
        recording: false,
        transcript: null as Transcript | null,
        prefilledSurveyAnswers: [] as PrefilledSurveyAnswer[],
        noteCount: null as number | null,
        loading: {
            customer: false,
            interview: false,
            attachments: false,
            surveys: false,
            transcript: false,
            prefilledSurveyAnswers: false,
            interviewInstance: false,
            carts: false,
        },
        consumerKey: null as string | null,
        customerId: null as number | null,
        backingId: null as number | null
    }),
    getters: {
        /**
         * Get the attachments for the healthcenter.
         */
        professionalAttachments(state): Attachment[] | null {
            if (state.attachments) {
                return state.attachments.filter((attachment) => {
                    return attachment.entity_types.map((entity) => entity.slug).includes('healthcenter');
                });
            }

            return state.attachments;
        },
        /**
         * Get the attachments for the patient.
         */
        patientAttachments(state): Attachment[] | null {
            if (state.attachments) {
                return state.attachments.filter((attachment) => {
                    return attachment.entity_types.map((entity) => entity.slug).includes('patient');
                });
            }
            return state.attachments;
        },
        /**
         * Get the consumer name from previously initialized patient store.
         */
        consumerName(state): string | null {
            const patientStore = usePatientStore();

            if (!state.consumerKey) {
                return null;
            }

            const consumer = patientStore.getConsumer(state.consumerKey);

            return consumer?.information?.name || "";
        },

        /**
         * Get the program that contains this interview.
         */
        program(state): Program | undefined {
            const patientStore = usePatientStore();

            if(state.customerId !== patientStore.customer?.id) {
                return undefined;
            }

            return patientStore.customer?.programs.find((_program) => {
                if (_program.backings?.length) {
                    return _program.backings.find((backing) => {
                        return backing.interviews.find(backingInterview => {
                            return backingInterview.id === state.interviewId;
                        });
                    });
                }

                return false;
            });
        },

        /**
         * Get the backing for the interview.
         */
        backing(state): Backing | undefined {
            return this.program?.backings?.find((_backing) => {
                return _backing.id === state.backingId;
            });
        },

        /**
         * Get the backing step for the current interview.
         */
        backingStep(state): BackingInterviewStep | undefined {
            const backing = this.backing;

            if (!backing || !state.interviewId) {
                return undefined;
            }

            return backing.steps.find((step) => {
                return step.resource_type === 'interview' && step.resource_id === state.interviewId;
            }) as BackingInterviewStep | undefined;
        }
    },
    actions: {
        setRecording(value: boolean): void {
            this.recording = value;
        },
        /**
         * Initialize the interview by reading the interview and fetching the attachments.
         */
        async initialize(interviewId: number, customerId: number, consumerKey: string, interviewInstanceId?: number, backingId?: number): Promise<void[]> {
            this.$reset();
            this.customerId = customerId;
            this.consumerKey = consumerKey;
            this.interviewId = interviewId;
            this.loading.interview = true;
            this.loading.attachments = true;
            this.loading.surveys = true;
            this.loading.carts = true;
            this.backingId = backingId || null;

            if (interviewInstanceId) {
                this.loading.interviewInstance = true;
            }

            this.loadCarts(consumerKey)
                .finally(() => this.loading.carts = false);

            return await Promise.all([
                this.initPatientStore(customerId),
                this.readInterview(interviewId),
                this.fetchAttachments(interviewId),
                this.fetchSurveys(),
                this.getNoteCount(),
                interviewInstanceId ? this.readInterviewInstance(interviewId, interviewInstanceId) : Promise.resolve()
            ]).finally(() => {
                this.loading.interview = false;
                this.loading.attachments = false;
                this.loading.surveys = false;
            });
        },
        /**
         * Read an interview by its ID.
         */
        async readInterview(interviewId: number): Promise<void> {
            return await interviewApi()
                .read(interviewId)
                .then((interview: Interview) => {
                    this.interview = interview;
                });
        },
        /**
         * Start the interview instance.
         */
        async startInterviewInstance(): Promise<InterviewInstance> {
            if (!this.interviewInstance) {
                await interviewApi()
                    .createInstance((this.interview?.id as number), (this.consumerKey as string), this.backingId)
                    .then((instance) => {
                        this.interviewInstance = instance;
                        this.updatePatientStore(instance);
                    });
            }

            return (this.interviewInstance as InterviewInstance);
        },
        /**
         * Fetch relevant attachments for an interview.
         */
        async fetchAttachments(interviewId: number): Promise<void> {
            this.attachments = await interviewApi()
                .attachments(interviewId, this.consumerKey)
                .then((attachments: Attachment[]) => {
                    return attachments.filter((attachment: Attachment) => attachment.relevant);
                });

        },
        /**
         * Fetch surveys.
         */
        async fetchSurveys(): Promise<void> {
            return await surveyApi()
                .index()
                .then(surveys => {
                    this.surveys = surveys.data;
                });
        },
        /**
         * Get the number of notes for the consumer.
         */
        async getNoteCount(): Promise<void> {
            return await interviewApi().notes((this.consumerKey as string), 1, 1)
                .then((data: { meta: { total: number }, data: [] }) => {
                    this.noteCount = data.meta.total;
                });
        },
        /**
         * Read the transcript for an interview.
         */
        async readTranscript(instanceId: number, audio: Blob): Promise<Transcript> {
            this.loading.transcript = true;

            try {
                const transcript = await interviewApi().transcript(instanceId, audio);

                transcript.text = transcript.utterances.map((utterance: Utterance) => utterance.text).join(' ');

                this.transcript = transcript;

                return transcript;
            } finally {
                this.loading.transcript = false;
            }
        },
        /**
         * Get the prefilled interview survey answers from the transcript + stored data.
         */
        async prefillInterviewSurvey(
            interviewId: number,
            interviewInstanceId: number,
            transcriptId: string | null = null,
            transcriptText: string | null = null
        ): Promise<void> {
            if (!this.consumerKey) {
                return;
            }

            this.loading.prefilledSurveyAnswers = true;
            const transcript = transcriptText ? {transcriptId, transcriptText} : null;

            this.prefilledSurveyAnswers = await interviewApi()
                .prefillInterviewSurveySync(interviewId, interviewInstanceId, this.consumerKey, transcript)
                .finally(() => {
                    this.loading.prefilledSurveyAnswers = false;
                });
        },
        /**
         * Add a note to the interview.
         */
        async addNote(content: string): Promise<void> {
            return await interviewApi()
                .storeNote((this.interviewInstance?.id as number), content)
                .then(() => interviewApi().notes((this.consumerKey as string), 1, 1)
                    .then((data: { meta: { total: number }, data: [] }) => {
                        this.noteCount = data.meta.total;
                    })
                );
        },

        /**
         * Read the interview instance.
         */
        async readInterviewInstance(interviewId: number, interviewInstanceId: number): Promise<void> {
            this.loading.interviewInstance = true;
            return await interviewApi().readInstance(interviewId, interviewInstanceId)
                .then(async (instance) => {
                    this.interviewInstance = instance;

                    if (instance.has_archive) {
                        const archive = await interviewApi().readArchive(instance.id);

                        if (archive.transcript) {
                            this.transcript = {
                                utterances: archive.transcript.split("\n").map((text: string) => ({text})),
                                text: archive.transcript
                            };
                        }
                    }
                })
                .finally(() => {
                    this.loading.interviewInstance = false;
                });
        },

        /**
         * Run the prefill survey job.
         *
         * If an audio is available in store, use it to read the transcript before running the prefill survey job.
         */
        async runPrefillSurveyJob(): Promise<void> {
            const instanceId = (this.interviewInstance?.id as number);
            const interviewId = (this.interview?.id as number);
            const consumerKey = (this.consumerKey as string);

            if (this.audio) {
                return this.readTranscript(instanceId, this.audio)
                    .then((_transcript) => {
                        return interviewApi()
                            .runPrefillSurveyJob(
                                interviewId,
                                instanceId,
                                consumerKey,
                                _transcript
                            );
                    });
            } else {
                return interviewApi()
                    .runPrefillSurveyJob(
                        interviewId,
                        instanceId,
                        consumerKey,
                        this.transcript
                    );
            }
        },

        /**
         * Init the patient store.
         */
        async initPatientStore(customerId: number) {
            const patientStore = usePatientStore();

            this.loading.customer = true;

            await patientStore.load(customerId);
            this.loading.customer = false;
        },

        /**
         * Load consumer carts.
         */
        async loadCarts(consumerKey: string): Promise<void> {
            return consumerCartApi()
                .index(consumerKey, null, {})
                .then((response) => {
                    this.carts = response.data;
                    this.cartAfterKey = response.meta.after_key || null;
                });
        },

        /**
         * Update the patient store with the interview instance for optimistic updates.
         * Adds or updates the instance in the backing step's interview_instances array.
         */
        updatePatientStore(instance: InterviewInstance) {
            const backingStep = this.backingStep;

            if (!backingStep) {
                return;
            }

            if (!backingStep.interview_instances) {
                backingStep.interview_instances = [];
            }

            const {interview: _, ...instanceWithoutInterview} = instance;

            const existingInstanceIndex = backingStep.interview_instances.findIndex(
                (existingInstance) => existingInstance.id === instance.id
            );

            if (existingInstanceIndex !== -1) {
                backingStep.interview_instances[existingInstanceIndex] = instanceWithoutInterview;
            } else {
                backingStep.interview_instances.push(instanceWithoutInterview);
            }

            if (instanceWithoutInterview.ended_at) {
                backingStep.completed_at = instanceWithoutInterview.ended_at;
            }
        }
    }
});

//https://pinia.vuejs.org/cookbook/hot-module-replacement.html#HMR-Hot-Module-Replacement-
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useInterviewStore, import.meta.hot));
}
